import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Plane, Map as MapIcon, Sparkles, Globe2, Compass, Star, Award, Flag, BookOpen, Sun } from 'lucide-react';
import { useCity } from '@/context/CityContext';

/* Convert an ISO 3166-1 alpha-2 code (e.g. "JP") into a flag emoji "🇯🇵". */
function flagEmoji(code) {
  if (!code || typeof code !== 'string' || code.length !== 2) return '🏳️';
  const A = 0x1F1E6; // regional indicator 'A'
  const u = code.toUpperCase().split('').map(c => A + (c.charCodeAt(0) - 65));
  if (u.some(x => x < A || x > A + 25)) return '🏳️';
  return String.fromCodePoint(...u);
}

function loadVisited() {
  try {
    const arr = JSON.parse(localStorage.getItem('gt_visited') || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

/* Achievement definitions — easy to extend later. */
const BADGES = [
  { id: 'first_trip',     label: 'First Trip',        icon: Plane,    test: s => s.cities >= 1, color: 'from-[#C4FF3D] to-[#9be635]' },
  { id: 'cities_3',       label: '3 Cities',          icon: MapIcon,  test: s => s.cities >= 3, color: 'from-[#F2A47A] to-[#e88a5b]' },
  { id: 'cities_5',       label: '5 Cities',          icon: Compass,  test: s => s.cities >= 5, color: 'from-[#DDEAD0] to-[#b7d9a5]' },
  { id: 'cities_10',      label: '10 Cities',         icon: Award,    test: s => s.cities >= 10, color: 'from-[#FFB400] to-[#F2A47A]' },
  { id: 'countries_3',    label: '3 Countries',       icon: Globe2,   test: s => s.countries >= 3, color: 'from-[#9DD9F3] to-[#5BB6E0]' },
  { id: 'countries_5',    label: '5 Countries',       icon: Flag,     test: s => s.countries >= 5, color: 'from-[#C9A6F0] to-[#8E6BD8]' },
  { id: 'countries_10',   label: 'Continent Hopper',  icon: Trophy,   test: s => s.countries >= 10, color: 'from-[#15291C] to-[#0F1A13]', dark: true },
  { id: 'diarist',        label: 'Diarist',           icon: BookOpen, test: s => s.diary >= 3, color: 'from-[#FFD86B] to-[#FFB400]' },
  { id: 'sun_seeker',     label: 'Sun Seeker',        icon: Sun,      test: s => s.tropical >= 1, color: 'from-[#FF8A3D] to-[#F2A47A]' },
  { id: 'globetrotter',   label: 'Globetrotter',      icon: Sparkles, test: s => s.countries >= 15, color: 'from-[#C4FF3D] via-[#F2A47A] to-[#FFB400]' },
];

/* A tiny list of country-code -> climate hint for the "Sun Seeker" badge.
   Cheap, no-network heuristic — extend as we please. */
const TROPICAL_CC = new Set([
  'TH','VN','ID','PH','MY','SG','LK','IN','MV','MX','CR','PA','CO','BR','PE','EC',
  'KE','TZ','UG','ZA','AU','FJ','PG','DO','JM','BS','CU','HN','GT','NI','SV','BZ',
]);

export default function PassportSouvenirs() {
  const { city } = useCity();
  const [visited, setVisited] = useState([]);
  const [diary, setDiary] = useState([]);

  const reload = () => {
    setVisited(loadVisited());
    try {
      const d = JSON.parse(localStorage.getItem('gt_diary') || '[]');
      setDiary(Array.isArray(d) ? d : []);
    } catch { setDiary([]); }
  };

  useEffect(() => {
    reload();
    const onSync = () => reload();
    window.addEventListener('trivly:cloud-sync', onSync);
    window.addEventListener('storage', onSync);
    return () => {
      window.removeEventListener('trivly:cloud-sync', onSync);
      window.removeEventListener('storage', onSync);
    };
  }, []);

  const stats = useMemo(() => {
    // De-dupe city|country pairs
    const seenCities = new Set();
    const countryMap = new Map(); // cc -> {name, cc, cities: Set}
    visited.forEach(v => {
      const cc = (v?.country_code || '').toUpperCase();
      const cityKey = `${v?.name || ''}|${cc}`;
      if (v?.name) seenCities.add(cityKey);
      if (cc) {
        if (!countryMap.has(cc)) countryMap.set(cc, { name: v.country || cc, cc, cities: new Set() });
        if (v?.name) countryMap.get(cc).cities.add(v.name);
      }
    });
    const countries = Array.from(countryMap.values()).sort((a, b) => b.cities.size - a.cities.size);
    return {
      cities: seenCities.size,
      countries: countries.length,
      countryList: countries,
      diary: diary.length,
      tropical: countries.filter(c => TROPICAL_CC.has(c.cc)).length,
    };
  }, [visited, diary]);

  const earned = BADGES.filter(b => b.test(stats));
  const next = BADGES.find(b => !b.test(stats));

  return (
    <div className="px-5 mt-5" data-testid="passport-souvenirs">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold">Souvenirs</p>
        <span className="text-[11px] text-[#7A8A80]">{stats.cities} cities · {stats.countries} countries</span>
      </div>

      {/* Stat strip */}
      <div className="glass-card p-4 mb-3" data-testid="souvenirs-stats">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="font-display text-2xl font-bold text-[#0F1A13]" data-testid="stat-cities">{stats.cities}</p>
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#7A8A80] font-bold mt-0.5">Cities</p>
          </div>
          <div className="border-x border-[#0F1A13]/08">
            <p className="font-display text-2xl font-bold text-[#0F1A13]" data-testid="stat-countries">{stats.countries}</p>
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#7A8A80] font-bold mt-0.5">Countries</p>
          </div>
          <div>
            <p className="font-display text-2xl font-bold text-[#0F1A13]" data-testid="stat-badges">{earned.length}</p>
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#7A8A80] font-bold mt-0.5">Badges</p>
          </div>
        </div>
      </div>

      {/* Flag wall */}
      {stats.countryList.length === 0 ? (
        <div className="glass-card p-4 text-[12.5px] text-[#4A5A50]" data-testid="souvenirs-flags-empty">
          Your flag wall is empty. Visit a city — current city <span className="font-semibold text-[#15291C]">{city?.name || ''}</span> will appear here once it's added to Visited.
        </div>
      ) : (
        <div className="mb-4">
          <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold mb-2">Flag wall</p>
          <div className="grid grid-cols-5 gap-2.5" data-testid="souvenirs-flag-grid">
            {stats.countryList.map((c, i) => (
              <motion.div
                key={c.cc}
                initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
                className="aspect-square rounded-2xl bg-white border border-[#0F1A13]/08 flex flex-col items-center justify-center gap-0.5 shadow-[0_4px_14px_rgba(15,26,19,0.08)] relative"
                title={`${c.name} — ${c.cities.size} ${c.cities.size === 1 ? 'city' : 'cities'}`}
                data-testid={`flag-${c.cc.toLowerCase()}`}
              >
                <span className="text-2xl leading-none">{flagEmoji(c.cc)}</span>
                <span className="text-[9px] font-bold uppercase tracking-wider text-[#15291C]">{c.cc}</span>
                {c.cities.size > 1 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#15291C] text-[#C4FF3D] text-[9px] font-bold flex items-center justify-center shadow-[0_2px_6px_rgba(15,26,19,0.25)]">
                    {c.cities.size}
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Badges */}
      <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold mb-2">Achievements</p>
      <div className="grid grid-cols-3 gap-2" data-testid="souvenirs-badge-grid">
        {BADGES.map((b, i) => {
          const Icon = b.icon;
          const got = b.test(stats);
          return (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.03 }}
              className={`relative rounded-2xl p-2.5 flex flex-col items-center gap-1 text-center border transition-all ${got
                ? `bg-gradient-to-br ${b.color} ${b.dark ? 'text-[#C4FF3D] border-[#15291C]' : 'text-[#0F1A13] border-[#0F1A13]/10'} shadow-[0_6px_18px_rgba(15,26,19,0.12)]`
                : 'bg-[#0F1A13]/04 text-[#7A8A80] border-dashed border-[#0F1A13]/15'
              }`}
              data-testid={`badge-${b.id}${got ? '-on' : '-off'}`}
            >
              <Icon className={`w-5 h-5 ${got ? '' : 'opacity-60'}`} strokeWidth={got ? 2.4 : 1.8} />
              <p className="text-[10px] font-bold leading-tight">{b.label}</p>
              {got && (
                <Star className={`w-3 h-3 absolute top-1 right-1 ${b.dark ? 'text-[#C4FF3D]' : 'text-[#15291C]'}`} fill="currentColor" />
              )}
            </motion.div>
          );
        })}
      </div>

      {next && (
        <p className="mt-3 text-[11.5px] text-[#4A5A50] text-center" data-testid="souvenirs-next-hint">
          <span className="font-semibold text-[#15291C]">Next:</span> {next.label} ✨
        </p>
      )}
    </div>
  );
}
